// SeoDoctorHelmet.jsx
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { baseImage_path } from "../variables/Variables";
import { stripHtmlForMeta } from "../utils/seoMetaText";

const SeoDoctorHelmet = ({ doctor }) => {
  const decodeHtmlEntities = (html) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    return textarea.value;
  };

  const extractScriptContent = (htmlString) => {
    const decoded = decodeHtmlEntities(htmlString);

    const matches = decoded.match(/<script[^>]*type=['"]?(.*?)['"]?[^>]*>([\s\S]*?)<\/script>/i);

    if (matches) {
      return {
        type: matches[1] || "text/javascript",
        content: matches[2].trim(),
      };
    }

    // Fallback: assume JS
    return {
      type: "text/javascript",
      content: decoded.trim(),
    };
  };

  // ✅ Dynamically inject and execute custom script
  useEffect(() => {
    if (!doctor?.custom_scripts) return;

    const decodeHtmlEntities = (html) => {
      const textarea = document.createElement("textarea");
      textarea.innerHTML = html;
      return textarea.value;
    };

    let decoded = decodeHtmlEntities(doctor.custom_scripts).trim();

    // Extract <script type="...">...</script>
    const match = decoded.match(
      /<script[^>]*type=["']?(.*?)["']?[^>]*>([\s\S]*?)<\/script>/i
    );

    let type = "text/javascript";
    let content = decoded;

    if (match) {
      type = match[1] || "text/javascript";
      content = match[2].trim();
    }  

     // ✅ Fallback: raw JSON without script tag
    if (!match && decoded.startsWith("{") && decoded.endsWith("}")) {
      try {
        JSON.parse(decoded);
        type = "application/ld+json";
        content = decoded;
      } catch (err) {
        console.error("❌ Invalid fallback JSON-LD", err);
        return;
      }
    }

    // ✅ If content *looks* like JSON, use type=application/ld+json
    const isJsonLd =
      type === "application/ld+json" ||
      (content.trim().startsWith("{") && content.trim().endsWith("}"));

    if (isJsonLd) {
      try {
        JSON.parse(content); // verify it’s valid JSON
        type = "application/ld+json"; // override if needed
      } catch (err) {
        console.error("❌ Invalid JSON-LD", err);
        return;
      }
    } else {
      // ⛔ If content is JSON but type is JS, stop here
      if (content.trim().startsWith("{")) {
        console.error(
          "❌ Detected JSON but not using application/ld+json. Abort script injection."
        );
        return;
      }
    }

    const existing = document.getElementById("seo-script");
    if (existing) existing.remove();

    const script = document.createElement("script");
    script.id = "seo-script";
    script.type = type;
    script.text = content;
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById("seo-script");
      if (el) el.remove();
    };
  }, [doctor?.custom_scripts]);

  if (!doctor) return null;

  const cleanDescription = stripHtmlForMeta(doctor.description);

  const title = doctor.seo_title || `${doctor.name_prefix || ""} ${doctor.name} | SL Raheja Hospital`;
  const description = stripHtmlForMeta(doctor.meta_description) || cleanDescription;
  const url = typeof window !== "undefined" ? window.location.href : "";

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={doctor.meta_title || title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={doctor.meta_keywords || "doctor, hospital, SL Raheja"} />

      <meta property="og:title" content={doctor.og_title || title} />
      <meta property="og:description" content={stripHtmlForMeta(doctor.og_description) || description} />
      <meta property="og:type" content={doctor.og_type || "profile"} />
      <meta property="og:url" content={doctor.og_url || url} />
      {doctor.og_image && (
        <meta property="og:image" content={`${baseImage_path}/${doctor.og_image}`} />
      )}
      <meta property="og:locale" content={doctor.og_locale || "en_US"} />
      <meta property="og:site_name" content={doctor.og_site_name || "SL Raheja Hospital"} />
      <meta property="article:modified_time" content={doctor.article_modified_time || new Date().toISOString()} />

      <meta name="twitter:card" content={doctor.twitter_card || "summary"} />
      <meta name="twitter:title" content={doctor.twitter_title || title} />
      <meta name="twitter:description" content={stripHtmlForMeta(doctor.twitter_description) || description} />
      {doctor.twitter_image && (
        <meta name="twitter:image" content={`${baseImage_path}/${doctor.twitter_image}`} />
      )}
      <meta name="twitter:site" content={doctor.twitter_site || "@SLRaheja"} />

      <link rel="canonical" href={doctor.canonical_link || url} />

   {/*      {doctor.custom_scripts && (
        <script
          type="text/javascript"
          dangerouslySetInnerHTML={{ __html: doctor.custom_scripts }}
        />
      )}  */}
    </Helmet>
  );
};

export default SeoDoctorHelmet;
